import type { CreateQueryActionData } from "../api";

export type SavedQueryAction = Omit<
  CreateQueryActionData["body"],
  "integrationId"
>;

const outputMappings: CreateQueryActionData["body"]["outputMappings"] = [
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% assign first = true %}\n{% for item in data %}\n  {% for target in item.target %}\n    {% if target.type == "AppInstance" %}\n      {% unless first %},{% endunless %}\n      "{{ target.displayName }}"\n      {% assign first = false %}\n    {% endif %}\n  {% endfor %}\n{% endfor %}\n]',
    outputType: "LIST",
    qualifiedName: "miruviz.metadata.applicationname",
    relationship: "toApplicationName",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.client.geographicalContext.geolocation.lat }},{{ item.client.geographicalContext.geolocation.lon }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.infrastructure.geolocation",
    relationship: "toGeolocation",
  },
  {
    isRoot: true,
    liquidTemplate:
      '[\n{% for item in data %}\n    {% if item.actor.alternateId %}\n        "{{ item.actor.alternateId }}"{% unless forloop.last %},{% endunless %}\n    {% endif %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.infrastructure.emailaddress",
    relationship: "toEmail",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.client.geographicalContext.country }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.infrastructure.countryname",
    relationship: "toCountry",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.client.ipAddress }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.infrastructure.ipv4address",
    relationship: "toIPAddress",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.device.id }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.infrastructure.device",
    relationship: "toDevice",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.published }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.metadata.eventtime",
    relationship: "toEventTime",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.uuid }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.metadata.eventid",
    relationship: "toEventID",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.outcome.result }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]',
    outputType: "LIST",
    qualifiedName: "miruviz.metadata.eventresult",
    relationship: "toEventResult",
  },
  {
    isRoot: false,
    liquidTemplate:
      '[\n{% for item in data %}\n    "{{ item.eventType }}"{% unless forloop.last %},{% endunless %}\n{% endfor %}\n]\n',
    outputType: "LIST",
    qualifiedName: "miruviz.metadata.eventtype",
    relationship: "toEventType",
  },
];

export const OktaQueryActions: SavedQueryAction[] = [
  {
    apiParameters: [
      {
        name: "filter",
        type: "QueryParameter",
        value:
          'eventType eq "user.session.start" and outcome.result eq "SUCCESS" and actor.id eq "<<miruviz.okta.userid>>"',
      },
      {
        name: "sortOrder",
        type: "QueryParameter",
        value: "DESCENDING",
      },
      {
        name: "since",
        type: "QueryParameter",
        value:
          '{{ "now" | date: "%s" | minus: 259200 | date: "%Y-%m-%dT%H:%M:%SZ" }}',
      },
    ],
    description:
      "List all System Log events by searching Okta Actor ID. The Okta System Log records system events that are related to your organization in order to provide an audit trail that can be used to understand platform activity and to diagnose problems.",
    method: "GET",
    name: "Okta Log Events 72hours - ActorID Search",
    outputMappings: outputMappings,
    requestPayload: "",
    url: "",
  },
  {
    apiParameters: [
      {
        name: "filter",
        type: "QueryParameter",
        value:
          'eventType eq "user.session.start" and outcome.result eq "SUCCESS" and actor.alternateId eq "<<miruviz.infrastructure.emailaddress>>"',
      },
      {
        name: "sortOrder",
        type: "QueryParameter",
        value: "DESCENDING",
      },
      {
        name: "since",
        type: "QueryParameter",
        value:
          '{{ "now" | date: "%s" | minus: 259200 | date: "%Y-%m-%dT%H:%M:%SZ" }}',
      },
    ],
    description:
      "List all System Log events by searching Email address. The Okta System Log records system events that are related to your organization in order to provide an audit trail that can be used to understand platform activity and to diagnose problems.",
    method: "GET",
    name: "Okta Log Events 72hours - Email Search",
    outputMappings: outputMappings,
    requestPayload: "",
    url: "",
  },
  {
    apiParameters: [
      {
        name: "filter",
        type: "QueryParameter",
        value:
          'eventType eq "user.session.start" and outcome.result eq "SUCCESS" and client.ipAddress eq "<<miruviz.infrastructure.ipv4address>>"',
      },
      {
        name: "sortOrder",
        type: "QueryParameter",
        value: "DESCENDING",
      },
      {
        name: "since",
        type: "QueryParameter",
        value:
          '{{ "now" | date: "%s" | minus: 259200 | date: "%Y-%m-%dT%H:%M:%SZ" }}',
      },
    ],
    description:
      "List all System Log events by searching for an IP address. The Okta System Log records system events that are related to your organization in order to provide an audit trail that can be used to understand platform activity and to diagnose problems.",
    method: "GET",
    name: "Okta Log Events 72hours - IP Search",
    outputMappings: outputMappings,
    requestPayload: "",
    url: "",
  },
  {
    apiParameters: [
      {
        name: "filter",
        type: "QueryParameter",
        value: 'actor.alternateId eq "<<miruviz.infrastructure.emailaddress>>"',
      },
      {
        name: "sortOrder",
        type: "QueryParameter",
        value: "DESCENDING",
      },
      {
        name: "since",
        type: "QueryParameter",
        value:
          '{{ "now" | date: "%s" | minus: 7776000 | date: "%Y-%m-%dT%H:%M:%SZ" }}',
      },
    ],
    description:
      "List all System Log events by searching Email address. The Okta System Log records system events that are related to your organization in order to provide an audit trail that can be used to understand platform activity and to diagnose problems.",
    method: "GET",
    name: "Okta Log Events 90days - Email Search",
    outputMappings: outputMappings,
    requestPayload: "",
    url: "",
  },
  {
    apiParameters: [
      {
        name: "filter",
        type: "QueryParameter",
        value:
          'eventType eq "user.session.start" OR eventType eq "system.api_token.create" OR eventType eq "user.mfa.factor.activate" OR eventType eq "user.mfa.factor.suspend" OR eventType eq "user.mfa.factor.update" OR eventType eq "user.mfa.attempt_bypass" OR eventType eq "user.account.reset_password" OR eventType eq "user.account.unlock" OR eventType eq "user.account.unlock_by_admin" OR eventtype eq "user.account.unlock_token" OR eventType eq "user.account.update_password" OR eventType eq "app.oauth2.token.reuse"',
      },
      {
        name: "sortOrder",
        type: "QueryParameter",
        value: "DESCENDING",
      },
      {
        name: "since",
        type: "QueryParameter",
        value: "<<runtime.date.since>>",
      },
    ],
    description:
      'List Okta "user.session.start", "system.api_token.create", and "user.mfa.factor.activate" System log events from a starting datetime',
    method: "GET",
    name: "Okta System Log",
    outputMappings: outputMappings,
    requestPayload: "",
    url: "",
  },
];
